import { inject, bindable } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import { PasskeyApi } from '../../apis/passkey/passkey-api';
import {
    prepareAssertionOptions, serializeAssertionCredential
} from '../../infrastructure/webauthn-helpers';

@inject(PasskeyApi, I18N)
export class PasskeyAuth {
    @bindable public onSuccess: () => void;

    public email: string = '';
    public isBusy: boolean = false;
    public errorMessage: string | null = null;

    public get isSupported(): boolean {
        return !!(window.PublicKeyCredential);
    }

    constructor(
        private readonly _passkeyApi: PasskeyApi,
        private readonly _i18n: I18N,
    ) {}

    public async signIn(): Promise<void> {
        if (!this.email) return;
        this.isBusy = true;
        this.errorMessage = null;
        try {
            const { options, cacheKey } = await this._passkeyApi.loginBegin(this.email);
            if (!options?.allowCredentials?.length) {
                this.errorMessage = this._i18n.tr('ui:passkey_not_registered');
                return;
            }
            const requestOptions = prepareAssertionOptions(options);
            const credential = await navigator.credentials.get({ publicKey: requestOptions }) as PublicKeyCredential | null;
            if (!credential) { this.isBusy = false; return; } // cancelled or intercepted by extension
            const assertion = serializeAssertionCredential(credential);
            await this._passkeyApi.loginComplete({ cacheKey, assertionResponse: JSON.stringify(assertion) });
            this.onSuccess?.();
        } catch (e: any) {
            if ((e as DOMException)?.name === 'NotAllowedError') { this.isBusy = false; return; }
            this.errorMessage = e?.content?.title ?? (typeof e?.content === 'string' ? e.content : null) ?? e?.message ?? (e?.statusCode ? `Error ${e.statusCode as number}` : String(e));
        } finally {
            this.isBusy = false;
        }
    }
}
